import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { Menu, X, ChevronDown } from "lucide-react";
import { PROPERTY_LIST } from "@/lib/properties";
import SocialIcons from "@/components/SocialIcons";

export default function Header({ variant = "home", propertyName, currentPropertyId }) {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [villasOpen, setVillasOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const homeNav = [
    { label: t("nav.villas"), href: "/#villas", isLink: false },
    { label: t("nav.story"), href: "/#historia", isLink: false },
    { label: t("activities.eyebrow"), href: "/#actividades", isLink: false },
    { label: t("nav.book"), href: "/#reservar", isLink: false },
    { label: "Blog", href: "/blog", isLink: true },
    { label: "Quiz", href: "/quiz", isLink: true },
    { label: "FAQ", href: "/#faq", isLink: false },
  ];
  const propertyNav = [
    { label: t("nav.story"), href: "#historia" },
    { label: t("nav.property"), href: "#la-casa" },
    { label: t("nav.experiences"), href: "#momentos" },
    { label: t("nav.gallery"), href: "#galeria" },
    { label: t("nav.location"), href: "#ubicacion" },
    { label: t("nav.book"), href: "#reservar" },
    { label: "Quiz", href: "/quiz", isLink: true },
  ];
  const nav = variant === "home" ? homeNav : propertyNav;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-stone-50/90 backdrop-blur-xl border-b border-stone-200/60" : "bg-transparent"
      }`}
    >
      {/* Top social bar — visible always, collapses in height when scrolled */}
      <div
        className={`transition-all duration-300 overflow-hidden ${
          scrolled ? "max-h-0 opacity-0" : "max-h-10 opacity-100"
        }`}
      >
        <SocialIcons variant="topbar" testPrefix="header" />
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
          <span className="font-serif-display text-xl md:text-2xl tracking-tight leading-none">
            Villas <span className="text-[#E07A5F]">Pura Vida</span>
          </span>
        </Link>

        {/* Villa switcher in property mode */}
        {variant === "property" && (
          <div className="relative hidden md:block">
            <button
              onClick={() => setVillasOpen((v) => !v)}
              onBlur={() => setTimeout(() => setVillasOpen(false), 150)}
              data-testid="villa-switcher-btn"
              className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                scrolled
                  ? "bg-stone-900 text-stone-50 hover:bg-[#E07A5F]"
                  : "bg-stone-50/95 backdrop-blur text-stone-900 hover:bg-white"
              }`}
            >
              <span className="text-eyebrow text-[10px] opacity-70">Viendo</span>
              <span className="font-serif-display text-base leading-none">{propertyName}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${villasOpen ? "rotate-180" : ""}`} />
            </button>
            {villasOpen && (
              <div
                data-testid="villa-switcher-menu"
                className="absolute top-full right-0 mt-2 w-80 bg-white border border-stone-200 shadow-xl"
              >
                {PROPERTY_LIST.map((p) => (
                  <Link
                    key={p.id}
                    to={`/villas/${p.slug}`}
                    data-testid={`switch-to-${p.id}`}
                    className={`block px-5 py-4 border-l-2 hover:bg-stone-50 transition-colors ${
                      p.id === currentPropertyId
                        ? "border-[#E07A5F] bg-stone-50"
                        : "border-transparent"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-serif-display text-lg text-stone-900">{p.name}</div>
                        <div className="text-xs text-stone-500 mt-0.5">
                          Hasta {p.capacity} huéspedes
                        </div>
                      </div>
                      {p.id === currentPropertyId && (
                        <span className="text-eyebrow text-[10px] text-[#E07A5F]">Actual</span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        <nav className="hidden lg:flex items-center gap-7">
          {nav.map((n) => {
            const isQuiz = n.label === "Quiz";
            const quizCls = "relative inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold tracking-wide text-white bg-gradient-to-r from-[#DC2626] to-[#EF4444] hover:from-[#B91C1C] hover:to-[#DC2626] transition-all hover:-translate-y-0.5 shadow-[0_6px_16px_-6px_rgba(220,38,38,0.6)]";
            const baseCls = "text-sm tracking-wide text-stone-700 hover:text-[#E07A5F] transition-colors";
            const cls = isQuiz ? quizCls : baseCls;
            return n.isLink ? (
              <Link
                key={n.href}
                to={n.href}
                data-testid={`nav-${n.href.replace(/[#/]/g, "")}`}
                className={cls}
              >
                {isQuiz && <span className="text-base leading-none">✨</span>}
                {n.label}
                {isQuiz && (
                  <span className="ml-1 text-[9px] uppercase tracking-[0.18em] bg-white/95 text-red-700 px-1.5 py-0.5 font-black leading-none">10% off</span>
                )}
              </Link>
            ) : (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.href.replace("#", "")}`}
                className={cls}
              >
                {n.label}
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="hidden sm:flex items-center gap-1 text-xs uppercase tracking-[0.2em] font-semibold text-stone-700 hover:text-[#E07A5F] transition-colors"
          >
            <span className={lang === "es" ? "text-[#E07A5F]" : ""}>ES</span>
            <span className="text-stone-300">/</span>
            <span className={lang === "en" ? "text-[#E07A5F]" : ""}>EN</span>
          </button>
          <a
            href={variant === "property" ? "#reservar" : "/#reservar"}
            data-testid="header-book-cta"
            className="hidden md:inline-flex bg-[#E07A5F] hover:bg-[#D3674B] text-white text-sm font-medium px-5 py-2.5 transition-all hover:-translate-y-0.5"
          >
            {t("nav.book")}
          </a>
          <button
            onClick={() => setOpen(true)}
            className="lg:hidden p-2 text-stone-800"
            data-testid="mobile-menu-btn"
            aria-label="menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-stone-50 z-50 lg:hidden overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-stone-200">
            <span className="font-serif-display text-2xl">
              Villas <span className="text-[#E07A5F]">Pura Vida</span>
            </span>
            <button onClick={() => setOpen(false)} data-testid="mobile-close-btn" aria-label="cerrar">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="p-6 border-b border-stone-200">
            <p className="text-eyebrow text-stone-500 mb-3">Las villas</p>
            <div className="space-y-2">
              {PROPERTY_LIST.map((p) => (
                <Link
                  key={p.id}
                  to={`/villas/${p.slug}`}
                  onClick={() => setOpen(false)}
                  className={`block px-4 py-3 border-l-2 ${
                    p.id === currentPropertyId ? "border-[#E07A5F] bg-stone-100" : "border-stone-300"
                  }`}
                >
                  <div className="font-serif-display text-lg">{p.name}</div>
                  <div className="text-xs text-stone-500">Hasta {p.capacity} huéspedes</div>
                </Link>
              ))}
            </div>
          </div>
          <nav className="flex flex-col gap-5 p-8 text-lg">
            {nav.map((n) => {
              const isQuiz = n.label === "Quiz";
              const quizCls = "inline-flex w-fit items-center gap-2 bg-gradient-to-r from-[#DC2626] to-[#EF4444] text-white font-bold px-4 py-2.5 shadow-[0_6px_16px_-6px_rgba(220,38,38,0.6)]";
              const baseCls = "text-stone-800 hover:text-[#E07A5F]";
              const cls = isQuiz ? quizCls : baseCls;
              return n.isLink ? (
                <Link key={n.href} to={n.href} onClick={() => setOpen(false)} className={cls}>
                  {isQuiz && <span>✨</span>}
                  {n.label}
                  {isQuiz && <span className="text-[10px] uppercase tracking-[0.15em] bg-white/95 text-red-700 px-1.5 py-0.5 font-black">10% off</span>}
                </Link>
              ) : (
                <a key={n.href} href={n.href} onClick={() => setOpen(false)} className={cls}>{n.label}</a>
              );
            })}
            <button onClick={() => { toggle(); setOpen(false); }} className="text-eyebrow text-stone-600 self-start">
              {lang === "es" ? "EN" : "ES"}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
